import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { FiArrowLeft, FiDownload, FiMail, FiPrinter } from 'react-icons/fi';
import { BACKEND_URL as BACKEND } from '../config';

export default function BillDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);

  useEffect(() => {
    api.get(`/bills/${id}`).then(r => { setBill(r.data.bill); setLoading(false); })
      .catch(() => { toast.error('Bill not found'); navigate('/bills'); });
  }, [id, navigate]);

  const downloadPDF = () => {
    const token = localStorage.getItem('token');
    window.open(`${BACKEND}/api/bills/${id}/pdf?token=${token}`, '_blank');
  };

  const sendEmail = async () => {
    if (!emailInput) return toast.error('Enter email address');
    setSendingEmail(true);
    try {
      await api.post(`/bills/${id}/send-email`, { email: emailInput });
      toast.success('Bill email sent!');
      setShowEmailModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Email failed');
    }
    setSendingEmail(false);
  };

  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (!bill) return null;

  const statusColor = { paid: '#10b981', pending: '#f59e0b', partial: '#3b82f6' };

  return (
    <div>
      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/bills')}>
          <FiArrowLeft size={14} /> Back
        </button>
        <button className="btn btn-outline btn-sm" onClick={downloadPDF}>
          <FiDownload size={14} /> Download PDF
        </button>
        <button className="btn btn-outline btn-sm" onClick={() => setShowEmailModal(true)}>
          <FiMail size={14} /> Send Email
        </button>
        <button className="btn btn-outline btn-sm" onClick={() => window.print()}>
          <FiPrinter size={14} /> Print
        </button>
      </div>

      {/* Bill Preview */}
      <div className="card bill-preview" style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #1e293b', paddingBottom: 20, marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1e293b' }}>INVOICE</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
              Bill #{bill.billNumber}
            </div>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              {new Date(bill.billDate).toLocaleDateString('en-AE', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
            <div style={{ marginTop: 8 }}>
              <span className={`badge badge-${bill.status === 'paid' ? 'success' : bill.status === 'partial' ? 'info' : 'warning'}`}>
                {bill.status.toUpperCase()}
              </span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 18, fontWeight: 700 }}>Customer</div>
            <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>{bill.customerName}</div>
            <div style={{ fontSize: 13, color: '#64748b' }}>{bill.customerPhone}</div>
          </div>
        </div>

        {/* Items Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
          <thead>
            <tr style={{ background: '#1e293b', color: 'white' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 12 }}>Description</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 12 }}>Qty</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 12 }}>Price</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 12 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {bill.items.map((item, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '10px 12px' }}>{item.description}</td>
                <td style={{ padding: '10px 12px', textAlign: 'center' }}>{item.quantity}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>SAR {item.price?.toFixed(2)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>SAR {item.total?.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: 280 }}>
            {[
              ['Subtotal', `SAR ${bill.subtotal?.toFixed(2)}`],
              [`VAT (${bill.vatPercent}%)`, `SAR ${bill.vatAmount?.toFixed(2)}`],
            ].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                <span>{l}</span><span>{v}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '2px solid #1e293b', fontWeight: 700, fontSize: 16 }}>
              <span>Grand Total</span><span>SAR {bill.total?.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13 }}>
              <span>Advance Paid</span><span style={{ color: '#10b981', fontWeight: 600 }}>SAR {bill.advancePaid?.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: bill.remaining > 0 ? '#fee2e2' : '#d1fae5', borderRadius: 6, fontWeight: 700 }}>
              <span>Remaining</span>
              <span style={{ color: bill.remaining > 0 ? '#ef4444' : '#10b981' }}>SAR {bill.remaining?.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* QR Code */}
        {bill.qrCode && (
          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
            <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'right' }}>
              <div>Scan to view digital bill</div>
              <div>{bill.emailSent ? '✅ Email sent' : '📧 Email not sent'}</div>
            </div>
            <img src={bill.qrCode} alt="QR" style={{ width: 80, height: 80 }} />
          </div>
        )}
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="modal-overlay" onClick={() => setShowEmailModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">📧 Send Bill via Email</h3>
              <button className="btn btn-outline btn-sm btn-icon" onClick={() => setShowEmailModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Customer Email Address</label>
                <input type="email" className="form-control" placeholder="customer@email.com"
                  value={emailInput} onChange={e => setEmailInput(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowEmailModal(false)}>Cancel</button>
              <button className="btn btn-accent" onClick={sendEmail} disabled={sendingEmail}>
                {sendingEmail ? 'Sending...' : 'Send Bill'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}